import {
  displayNameSchema,
  timeZoneSchema,
  type CurrentUser,
  type Locale,
  type UpdateMyProfileRequest,
} from '@mfos/shared';
import { type SubmitEvent, useState } from 'react';
import { ErrorNotice, LoadingNotice, SuccessNotice } from '../../components/ErrorNotice';
import { SelectField, TextField } from '../../components/Field';
import { useI18n } from '../../lib/i18n/runtime';
import { useCurrentUser, useUpdateProfile } from '../auth/auth.api';

const LOCALE_OPTIONS: readonly { value: Locale; label: string }[] = [
  { value: 'EN', label: 'English' },
  { value: 'HY', label: 'Հայերեն (Armenian)' },
  { value: 'RU', label: 'Русский (Russian)' },
];

function isLocale(value: string): value is Locale {
  return LOCALE_OPTIONS.some((option) => option.value === value);
}

export function ProfilePage() {
  const me = useCurrentUser();
  const { t } = useI18n();
  if (me.isPending) {
    return <LoadingNotice>{t('loadingSession')}</LoadingNotice>;
  }
  if (me.isError) {
    return <ErrorNotice error={me.error} />;
  }
  return <ProfileForm user={me.data} />;
}

function ProfileForm({ user }: { user: CurrentUser }) {
  const { locale, setLocale, t } = useI18n();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [timezone, setTimezone] = useState(user.timezone);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nothingChanged, setNothingChanged] = useState(false);
  const mutation = useUpdateProfile();
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const body: { -readonly [K in keyof UpdateMyProfileRequest]: UpdateMyProfileRequest[K] } = {};
    const name = displayNameSchema.safeParse(displayName);
    if (!name.success) {
      nextErrors.displayName = t('nameError');
    } else if (name.data !== user.displayName) {
      body.displayName = name.data;
    }
    const zone = timeZoneSchema.safeParse(timezone.trim());
    if (!zone.success) {
      nextErrors.timezone = t('zoneError');
    } else if (zone.data !== user.timezone) {
      body.timezone = zone.data;
    }
    if (locale !== user.locale) {
      body.locale = locale;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    if (Object.keys(body).length === 0) {
      setNothingChanged(true);
      return;
    }
    setNothingChanged(false);
    mutation.mutate(body);
  }

  return (
    <div className="stack">
      <h1>{t('profile')}</h1>
      <form onSubmit={submit} noValidate className="card stack">
        <p className="meta">
          {t('signedInAs')} {user.email}
        </p>
        <TextField
          label={t('displayName')}
          autoComplete="name"
          value={displayName}
          onValueChange={setDisplayName}
          error={errors.displayName}
          required
        />
        <SelectField
          label={t('language')}
          value={locale}
          onValueChange={(value) => {
            if (isLocale(value)) {
              setLocale(value);
            }
          }}
          options={LOCALE_OPTIONS}
          hint={t('languageHint')}
        />
        <TextField
          label={t('timeZone')}
          value={timezone}
          onValueChange={setTimezone}
          error={errors.timezone}
          hint={
            <>
              {t('timeZoneHint')}{' '}
              {browserZone !== timezone && (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setTimezone(browserZone);
                  }}
                >
                  {t('useDeviceZone')} ({browserZone})
                </button>
              )}
            </>
          }
          required
        />
        {nothingChanged && (
          <p className="meta" role="status">
            {t('nothingToSave')}
          </p>
        )}
        <ErrorNotice error={mutation.error} />
        {mutation.isSuccess && <SuccessNotice>{t('profileSaved')}</SuccessNotice>}
        <div className="form-actions">
          <button type="submit" className="button" disabled={mutation.isPending}>
            {mutation.isPending ? t('saving') : t('saveProfile')}
          </button>
        </div>
      </form>
    </div>
  );
}
